import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  updateWarehouseData,
  updateQualityData,
  updateItemWarehouseData,
  updateItemQualityData,
  updateProcurementAdjustments,
  WarehouseAdjustmentData,
  QualityAdjustmentData,
  ItemWarehouseAdjustmentData,
  ItemQualityAdjustmentData,
} from '@/services/adminProcurementAdjustmentService';

export function useUpdateWarehouseData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      procurementId,
      data,
      reason,
    }: {
      procurementId: string;
      data: WarehouseAdjustmentData;
      reason: string;
    }) => {
      return updateWarehouseData(procurementId, data, reason);
    },
    onSuccess: (_, variables) => {
      toast.success('Warehouse data adjusted successfully');
      queryClient.invalidateQueries({ queryKey: ['procurement', variables.procurementId] });
      queryClient.invalidateQueries({ queryKey: ['procurements'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to adjust warehouse data');
    },
  });
}

export function useUpdateQualityData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      procurementId,
      data,
      reason,
    }: {
      procurementId: string;
      data: QualityAdjustmentData;
      reason: string;
    }) => {
      return updateQualityData(procurementId, data, reason);
    },
    onSuccess: (_, variables) => {
      toast.success('Quality data adjusted successfully');
      queryClient.invalidateQueries({ queryKey: ['procurement', variables.procurementId] });
      queryClient.invalidateQueries({ queryKey: ['procurements'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to adjust quality data');
    },
  });
}

export function useUpdateItemWarehouseData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      procurementId,
      itemId,
      data,
      reason,
    }: {
      procurementId: string;
      itemId: string;
      data: ItemWarehouseAdjustmentData;
      reason: string;
    }) => {
      return updateItemWarehouseData(procurementId, itemId, data, reason);
    },
    onSuccess: (_, variables) => {
      toast.success('Item warehouse data adjusted successfully');
      queryClient.invalidateQueries({ queryKey: ['procurement', variables.procurementId] });
      queryClient.invalidateQueries({ queryKey: ['procurements'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to adjust item warehouse data');
    },
  });
}

export function useUpdateItemQualityData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      procurementId,
      itemId,
      data,
      reason,
    }: {
      procurementId: string;
      itemId: string;
      data: ItemQualityAdjustmentData;
      reason: string;
    }) => {
      return updateItemQualityData(procurementId, itemId, data, reason);
    },
    onSuccess: (_, variables) => {
      toast.success('Item quality data adjusted successfully');
      queryClient.invalidateQueries({ queryKey: ['procurement', variables.procurementId] });
      queryClient.invalidateQueries({ queryKey: ['procurements'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to adjust item quality data');
    },
  });
}

export function useProcurementAdjustments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      procurementId,
      warehouseData,
      qualityData,
      reason,
    }: {
      procurementId: string;
      warehouseData: WarehouseAdjustmentData | null;
      qualityData: QualityAdjustmentData | null;
      reason: string;
    }) => {
      return updateProcurementAdjustments(procurementId, warehouseData, qualityData, reason);
    },
    onSuccess: (_, variables) => {
      toast.success('Procurement data adjusted successfully');
      queryClient.invalidateQueries({ queryKey: ['procurement', variables.procurementId] });
      queryClient.invalidateQueries({ queryKey: ['procurements'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to adjust procurement data');
    },
  });
}
